import { useTranslation } from 'react-i18next';
import message from 'antd/es/message';
import React, { useCallback, useMemo, useState, ReactNode, useEffect } from 'react';
import cx from 'clsx';
import ReactGA4 from 'react-ga4';

import { IconRcBack } from '@/assets/overview';
import { Button, Step, Steps, Modal, UserAvatar } from '@/components';
import {
  useConnectDict,
  useConnectedAddress,
  useCurrentAddressInfo,
  fetchUserInfo,
  useSwitch,
  // useUserInfo,
  getGaUserType,
} from '@/hooks';
import { toastError } from '@/utils/error';
import { history } from '../../history';
import { afterVerifySignAddress, setCurrentAddress } from '../../address';
import { wrapModalPromise, WrapModalProps } from '../../modal';
import { shorten } from '../../string';
import { getCurrentNetVersion, pcConnect, web3UserSign } from '../../web3';
import { ProfileUSDValue } from '../../math';
import { useLogin } from './hook';
import style from './index.module.scss';
import { ReCaptchaModal } from '@/components/ReCaptchaModal';
import { addEmailLogin } from '@/components/RabbykitProvider';
import { loginGa } from '@/utils';
import { useLocation } from 'react-router-dom';

type SignLoginModalProps = {
  onBackClick?(): void;
  hasConnected?: boolean;
};

export const SignLoginModal = ({
  hide,
  onResolve,
  onBackClick,
  hasConnected,
}: SignLoginModalProps & WrapModalProps) => {
  const { t } = useTranslation();

  const { address: currentAddress } = useCurrentAddressInfo();
  const { pathname } = useLocation();
  const connectedAddress = useConnectedAddress();
  const connectedDict = useConnectDict();
  const isVerified = useMemo(
    () => connectedAddress && connectedDict[connectedAddress]?.sessionId,
    [connectedAddress, connectedDict],
  );
  // 是否是在hi Thirdparty 页面登录
  const isThirdpartyLogin = pathname === '/hi-thirdparty';

  const { mutateAsync } = useLogin();
  const [loading, setLoading] = useState<boolean>();
  const [step, setStep] = useState(!connectedAddress ? 0 : !isVerified ? 1 : 2);
  const [showAddess, setShowAddress] = useState(connectedAddress);
  const [error, setError] = useState(false);
  const { on, turnOff, turnOn } = useSwitch();
  // const { data: userInfo } = useUserInfo(connectedAddress);

  // const needRecaptcha = useMemo(
  //   () => !(userInfo?.desc?.user?.web3_id || (userInfo?.desc?.usd_value ?? 0) > 1000),
  //   [userInfo],
  // );
  const needRecaptcha = true;

  const handleViewProfile = useCallback(() => {
    loginGa({ hasConnected, action: 'Login_Skip' });
    if (isThirdpartyLogin) {
      ReactGA4.event({
        category: 'Hi Thirdparty',
        action: `Connected_Login_Skip_NoLogin`,
      });
    }
    history.push(`/profile/${connectedAddress}`);
    hide();
  }, [connectedAddress, hasConnected, hide, isThirdpartyLogin]);

  const handleLogin = useCallback(
    async (token?: string) => {
      setLoading(true);
      try {
        let address = connectedAddress;

        if (!address) {
          addEmailLogin();
          address = await pcConnect({ isLogin: true });
          setError(!address);

          if (address) {
            setShowAddress(address);
            setStep(1);
          }
          return;
        }

        // agree connect
        if (address) {
          if (needRecaptcha && !token) {
            turnOn();
            return;
          }
          loginGa({ hasConnected, action: 'Login_Verify' });
          if (isThirdpartyLogin) {
            ReactGA4.event({
              category: 'Hi Thirdparty',
              action: `Connected_Login_Verify_NoLogin`,
            });
          }
          const userType = getGaUserType(); // 登陆前的userType，用于判断是首次成功登录的还是已登录用户二次登录
          const userDetail = await fetchUserInfo(address);
          const lowerAddress = address.toLowerCase();
          const signature = await web3UserSign(lowerAddress);
          const chainId = await getCurrentNetVersion();
          const session = await mutateAsync({
            signature,
            id: lowerAddress,
            token,
            chain_id: chainId,
          });

          afterVerifySignAddress(lowerAddress, {
            session_id: session.session_id,
            userInfo: userDetail.user,
          });

          setCurrentAddress(lowerAddress);
          loginGa({ hasConnected, action: 'Login_Complete', userType });
          if (isThirdpartyLogin) {
            ReactGA4.event({
              category: 'Hi Thirdparty',
              action: `Connected_Login_Complete_NoLogin`,
            });
          }
          onResolve(lowerAddress);
          message.info(t('SignLoginModal.logged'));
          hide();
        }
      } catch (error) {
        const err = error as any;
        toastError(err);
        setError(true);
      } finally {
        setLoading(false);
      }
    },
    [
      connectedAddress,
      needRecaptcha,
      hasConnected,
      isThirdpartyLogin,
      mutateAsync,
      onResolve,
      t,
      hide,
      turnOn,
    ],
  );

  const handleRecaptchaChange = useCallback(
    (token: string | null) => {
      if (token) {
        turnOff();
        handleLogin(token);
      }
    },
    [handleLogin, turnOff],
  );

  useEffect(() => {
    if (hasConnected) {
      loginGa({ hasConnected, action: 'Login_Begin' });
    }
  }, [hasConnected]);

  if (isVerified && !loading) {
    const id = connectedAddress!.toLocaleLowerCase();

    if (currentAddress?.toLowerCase() === id) {
      const user = connectedDict[id];
      const userId = user.userInfo?.web3_id || shorten(id, 6, 4);
      const netWorth = ProfileUSDValue(user?.userInfo?.desc?.usd_value || 0);

      Modal.info({
        content: (
          <>
            <div className={style.alreadyTitle}>{t('SignLoginModal.ofqj47')}</div>
            <UserLoginInfo
              id={id}
              userId={userId}
              userDesc={user.userInfo?.desc}
              l2Balance={user.l2Balance}
              netWorth={netWorth}
              className={style.userInfo}
            />
          </>
        ),
      });
    } else {
      message.info(t('SignLoginModal.logged'));
      setCurrentAddress(connectedAddress!);
    }
    onResolve(connectedAddress);
    hide();

    return null;
  }

  return (
    <>
      <div className={style.loginModal}>
        <div className={style.header}>
          {onBackClick ? <IconRcBack className={style.backIcon} onClick={onBackClick} /> : null}
          <div className={style.modalTitle}>{t('SignLoginModal.wjpu0')}</div>
        </div>
        <Steps current={step} className={style.steps} loading={loading}>
          <Step
            title={showAddess ? t('SignLoginModal.connected') : t('SignLoginModal.connect-wallet')}
            desc={showAddess ? shorten(showAddess, 6, 4) : t('SignLoginModal.step1')}
          />
          <Step title={t('SignLoginModal.1ydx4r1')} desc={t('SignLoginModal.step2')} />
        </Steps>
        <Button
          type="primary"
          className={style.loginBtn}
          onClick={() => handleLogin()}
          disabled={step > 1}
          loading={loading}>
          {error
            ? t('SignLoginModal.try-again')
            : step > 0
              ? t('SignLoginModal.verify')
              : t('SignLoginModal.connect')}
        </Button>
        {connectedAddress ? (
          <div className={style.viewProfileWrap}>
            <span onClick={handleViewProfile} className={style.viewProfile}>
              {t('SignLoginModal.skip')}
            </span>
          </div>
        ) : null}
      </div>
      <ReCaptchaModal
        on={on}
        turnOff={turnOff}
        onChange={handleRecaptchaChange}
        action="USER_LOGIN"
      />
    </>
  );
};

export const signLogin = wrapModalPromise<SignLoginModalProps, void>(SignLoginModal);

type UserLoginInfoProps = {
  className?: string;
  onClick?(e: React.MouseEvent<HTMLDivElement>): void;
  l2Balance?: number;
  netWorth?: string;
  userId?: string;
  userDesc?: UserDesc;
  proStatus?: number;
  idx?: number;
  id: string;
  titleSlot?: ReactNode;
  titleEndSlot?: ReactNode;
};

export const UserLoginInfo = ({
  className,
  onClick,
  userId,
  userDesc,
  id,
  l2Balance,
  netWorth,
  idx,
  titleSlot,
  titleEndSlot,
}: UserLoginInfoProps) => {
  const { t } = useTranslation();

  return (
    <div className={cx(style.userItem, className)} data-id={id} data-idx={idx} onClick={onClick}>
      <UserAvatar data={userDesc} size={36} />
      <div className={style.userInfo}>
        <div className={style.userIdWrap}>
          <div className={style.userId}>
            <div className={style.userIdText}>{userId}</div>
            {titleSlot}
          </div>
          {titleEndSlot}
        </div>
        <div className={style.userAsset}>
          <span className={style.userAssetTitle}>{t('UserInfoBtn_LoginedUserList.q6o2ej')}</span>
          <span className={style.userAssetDesc}>{netWorth}</span>
        </div>
        <div className={style.userAsset}>
          <span className={style.userAssetTitle}>{t('UserInfoBtn_LoginedUserList.rhvciq')}</span>
          <span className={style.userAssetDesc}>{ProfileUSDValue(l2Balance || 0, 2)}</span>
        </div>
      </div>
    </div>
  );
};
